import { yupResolver } from '@hookform/resolvers/yup';
import type { CountryCode } from 'libphonenumber-js/max';
import { useEffect, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { CheckboxField } from '@/components/CheckboxField';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import {
  getTitleOptionByValue,
  getUserTitleLabel,
  TitleField,
} from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { Loader } from '@/components/Loader';
import { navToFindStores } from '@/components/Megamenu/helpers';
import { Modal } from '@/components/Modal';
import { NextImage } from '@/components/NextImage';
import { ReCaptcha } from '@/components/ReCaptcha';
import { Select } from '@/components/Select';
import { SelectField } from '@/components/SelectField';
import { TextAreaField } from '@/components/TextAreaField';
import { Typography } from '@/components/Typography';
import { Visibility } from '@/components/Visibility';
import type {
  Maybe,
  MoneyFragment,
  ProductVariantWatchBasicDetailsFragment,
} from '@/graphql/saleor/api';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { watchAvail, watchAvailGuest } from '@/lib/availability/helper';
import { SALESFORCE_TEXT_MAX_LENGTH } from '@/lib/forms/const';
import { getExamplePhone } from '@/lib/phone';
import {
  getPhonePrefixCode,
  getPhonePrefixSelectOpts,
} from '@/lib/phonePrefixes/helpers';
import { getWatchCollectionPath } from '@/lib/product';
import { getRegionFromLocale } from '@/lib/regions';
import {
  commonMessages as appointmentMessages,
  KeepInformed,
} from '@/messages/appointment';
import { commonMessages } from '@/messages/common';
import { formMessages, PersonalDataPolicy } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import type {
  RegionCityOption,
  RetailerOption,
} from '@/views/AppointmentView/helpers';
import {
  makeCityOptions,
  makeRegionOptions,
  useBoutiqueSelect,
} from '@/views/AppointmentView/helpers';

import type { AvailabilityRequestSchema } from './helper';
import { availabilityRequestSchema, getStores } from './helper';

import * as S from './AvailabilityRequestModal.styles';
import { ConfirmLegal, messages, ThankYouMessage } from './messages';

interface AvailabilityRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  price: Maybe<MoneyFragment>;
  product: ProductVariantWatchBasicDetailsFragment['product'];
}

export const AvailabilityRequestModal = ({
  isOpen,
  onClose,
  price,
  product,
}: AvailabilityRequestModalProps) => {
  const { formatMessage } = useIntl();
  const {
    country,
    formatPrice,
    locale: { sfCode },
    query: { languageCode },
  } = useRegionProvider();
  const { locale, push } = useRouter();
  const {
    locale: { slug: localeSlug },
  } = getRegionFromLocale(locale);
  const {
    snapshot: { isAuthenticated, user },
  } = useUser();
  const phonePrefixSelectOpts = getPhonePrefixSelectOpts();

  const countryCode = country.code;
  const selectPlaceholderColor = '#000000';

  const {
    boutiqueSelectState,
    handleChangeBoutique,
    handleChangeCity,
    handleChangeRegion,
    setBoutiqueSelectState,
  } = useBoutiqueSelect(localeSlug);
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [state, setState] = useState<'submitted' | 'inited' | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [examplePhone, setExamplePhone] = useState<string>('');

  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const schema = availabilityRequestSchema(formatMessage, country);
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const disabledPersonal = !!isAuthenticated && !!user;

  const init = async () => {
    setIsLoading(true);
    setError('');
    if (!isAuthenticated || !user) {
      formCtx.reset(schema.getDefault());
      reCaptchaRef.current?.reset();
    }
    try {
      const allStores = await getStores(countryCode, localeSlug);
      const regionOpts = makeRegionOptions(allStores, formatMessage);
      const sortedCityOpts = makeCityOptions(allStores);
      setBoutiqueSelectState({
        boutiqueList: allStores,
        regionOptions: regionOpts,
        cityOptions: sortedCityOpts,
      });
    } catch (e) {
      setBoutiqueSelectState({
        boutiqueList: [],
        regionOptions: [],
        cityOptions: [],
      });
    }
    setState('inited');
    setIsLoading(false);
    setBoutiqueSelectState({
      boutique: null,
      region: null,
      city: null,
    });
  };

  useEffect(() => {
    if (isOpen) {
      init();
    }
  }, [isOpen]);

  const personalTitle = user?.metadata?.find(
    (item) => item.key === 'user.personalTitle',
  )?.value;

  useEffect(() => {
    if (user) {
      const titleOption = getTitleOptionByValue(
        formatMessage,
        country.code,
        personalTitle,
      );
      formCtx.setValue('title', titleOption);
      formCtx.setValue('firstName', user.firstName ?? '');
      formCtx.setValue('lastName', user.lastName ?? '');
      formCtx.setValue('email', user.email ?? '');

      const userPhonePrefix = user?.metadata?.find(
        (item) => item.key === 'user.phonePrefix',
      )?.value;
      const userPhone = user?.metadata?.find(
        (item) => item.key === 'user.phone',
      )?.value;
      const prefixOption = phonePrefixSelectOpts.find(
        (item) => item.value === userPhonePrefix,
      );
      if (prefixOption && userPhone) {
        formCtx.setValue('phone', userPhone);
        formCtx.setValue('phonePrefix', {
          label: prefixOption.label,
          value: prefixOption.value,
        });
      }
    }
  }, [user, isOpen]);

  const captcha = formCtx.watch('captcha');
  const title = formCtx.watch('title');
  const lastName = formCtx.watch('lastName');
  const phone = formCtx.watch('phone');
  const phonePrefix = formCtx.watch('phonePrefix');
  useEffect(() => {
    if (phonePrefix) {
      const phoneNumberExample = getExamplePhone(
        getPhonePrefixCode(phonePrefix.value) as CountryCode,
      );
      if (phoneNumberExample) setExamplePhone(phoneNumberExample);
    }
  }, [phonePrefix]);

  const handleSubmit: SubmitHandler<AvailabilityRequestSchema> = async ({
    comments,
    email,
    firstName,
    lastName,
    newsletterOptIn,
    phone,
    phonePrefix,
    title,
  }) => {
    if (!boutiqueSelectState.boutique) {
      setError(formatMessage(messages.errorChooseYourRetailer));
      return;
    }
    const params = {
      boutiqueId: boutiqueSelectState.boutique.value,
      comments,
      email,
      firstName,
      languageCode: sfCode,
      lastName,
      newsletterOptIn,
      productSku: product.slug,
      recaptchaResponse: reCaptchaRef.current?.getValue() ?? '',
      salutation: title?.value ?? '',
      websiteCountryCode: countryCode,
      ...(phone && {
        phone: phonePrefix.value + phone,
      }),
    };

    setProcessing(true);
    try {
      let res;
      if (disabledPersonal) {
        res = await watchAvail(params);
      } else {
        res = await watchAvailGuest(params);
      }
      setProcessing(false);
      if (res.code === 200) {
        setState('submitted');
      } else {
        setError(res.type);
      }
    } catch (error) {
      setProcessing(false);
      setError(error instanceof Error ? error.message : 'System error.');
    }
  };

  return (
    <Modal gutters={true} isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        {formatMessage(commonMessages.availabilityRequest)}
      </Modal.Header>
      <Modal.Body>
        {isLoading && <Loader height={450} />}
        {!isLoading && (
          <Box>
            {boutiqueSelectState.boutiqueList.length > 0 && (
              <S.ProductInfo>
                <S.ProductImageWrapper>
                  <NextImage
                    alt={product.slug}
                    fill={true}
                    sizes='50vw'
                    src={product.thumbnail?.url ?? ''}
                  />
                </S.ProductImageWrapper>
                <S.ProductCardDetails>
                  <Typography color='secondaryGrey'>{product.slug}</Typography>
                  <S.ProductName scale='p1'>{product.name}</S.ProductName>
                  <Typography scale='p2'>
                    {product.caseMaterial?.values[0]?.name
                      ? product.caseMaterial.values[0].name + '-'
                      : ''}
                    {product.dialColor?.values[0]?.name
                      ? product.dialColor.values[0]?.name
                      : ''}
                  </Typography>
                  {price && (
                    <Typography>
                      {formatPrice({
                        amount: price.amount,
                        currency: price.currency,
                      })}
                    </Typography>
                  )}
                </S.ProductCardDetails>
              </S.ProductInfo>
            )}
            {state === 'submitted' && (
              <S.ResultWrapper>
                <S.Title>
                  {getUserTitleLabel(countryCode, title)}
                  {lastName}
                </S.Title>
                <Typography mt={2}>{ThankYouMessage}</Typography>
                <LegacyButton
                  label={formatMessage(commonMessages.discoverTheCollection)}
                  mt={2}
                  onClick={() => {
                    push(getWatchCollectionPath(product));
                  }}
                />
              </S.ResultWrapper>
            )}
            {boutiqueSelectState.boutiqueList.length === 0 && (
              <S.ResultWrapper>
                <Typography textAlign='center'>
                  {formatMessage(appointmentMessages.noAvailableBoutiques)}
                </Typography>
                <Typography textAlign='center'>
                  {formatMessage(appointmentMessages.storeLocatorToBrowse)}
                </Typography>
                <LegacyButton
                  label={formatMessage(messages.storeLocator)}
                  mt={2}
                  onClick={() => {
                    push(navToFindStores(locale, countryCode));
                  }}
                />
              </S.ResultWrapper>
            )}

            <Visibility
              xsHidden={
                state !== 'inited' ||
                boutiqueSelectState.boutiqueList.length === 0
              }
            >
              <Box>
                <S.Title as='h4'>
                  {formatMessage(appointmentMessages.chooseRetailer)}
                </S.Title>
                <S.RetailerSelectWrapper>
                  {boutiqueSelectState.regionOptions.length > 0 && (
                    <Select
                      borderColorVariant='lightGray'
                      colorVariant='secondaryLightGrey'
                      label={formatMessage(
                        appointmentMessages.selectRegionPlaceholder,
                      )}
                      name='region'
                      onChange={(opt) => {
                        handleChangeRegion(opt as RegionCityOption);
                      }}
                      options={boutiqueSelectState.regionOptions}
                      placeholder={formatMessage(
                        appointmentMessages.selectRegionPlaceholder,
                      )}
                      styles={{
                        placeholder: (baseStyles) => ({
                          ...baseStyles,
                          color: selectPlaceholderColor,
                        }),
                      }}
                      value={boutiqueSelectState.region}
                    />
                  )}
                  <Select
                    borderColorVariant='lightGray'
                    colorVariant='secondaryLightGrey'
                    label={formatMessage(
                      appointmentMessages.selectCityPlaceholder,
                    )}
                    isDisabled={boutiqueSelectState.cityOptions.length === 0}
                    name='city'
                    onChange={(opt) => {
                      handleChangeCity(opt as RegionCityOption);
                    }}
                    options={boutiqueSelectState.cityOptions}
                    placeholder={formatMessage(
                      appointmentMessages.selectCityPlaceholder,
                    )}
                    styles={{
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        ...(boutiqueSelectState.cityOptions.length !== 0 && {
                          color: selectPlaceholderColor,
                        }),
                      }),
                    }}
                    value={boutiqueSelectState.city}
                  />
                  <Select
                    borderColorVariant='lightGray'
                    colorVariant='secondaryLightGrey'
                    label={formatMessage(messages.selectRetailerPlaceholder)}
                    isDisabled={
                      boutiqueSelectState.boutiqueOptions.length === 0
                    }
                    name='boutique'
                    onChange={(opt) => {
                      handleChangeBoutique(opt as RetailerOption);
                      setError('');
                    }}
                    options={boutiqueSelectState.boutiqueOptions}
                    placeholder={formatMessage(
                      messages.selectRetailerPlaceholder,
                    )}
                    styles={{
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        ...(boutiqueSelectState.boutiqueOptions.length !==
                          0 && {
                          color: selectPlaceholderColor,
                        }),
                      }),
                    }}
                    value={boutiqueSelectState.boutique}
                    variant='default'
                  />
                </S.RetailerSelectWrapper>
                <S.Title as='h4'>
                  {formatMessage(formMessages.personalData)}
                </S.Title>
                <FormProvider
                  name='availability-request'
                  {...formCtx}
                  onSubmit={handleSubmit}
                >
                  <S.FormWrapper gapDensity='none'>
                    <Col md={6}>
                      <TitleField
                        colorVariant='grey'
                        countryCode={country.code}
                        isDisabled={disabledPersonal && !!personalTitle}
                      />
                    </Col>

                    <Col>
                      <Flex gridGap={2}>
                        <InputField
                          autoComplete='given-name'
                          borderColorVariant='lightGray'
                          colorVariant='secondaryLightGrey'
                          disabled={disabledPersonal}
                          maxLength={50}
                          name='firstName'
                          placeholder={formatMessage(formMessages.firstName)}
                          required
                        />
                        <InputField
                          autoComplete='additional-name'
                          borderColorVariant='lightGray'
                          colorVariant='secondaryLightGrey'
                          disabled={disabledPersonal}
                          maxLength={80}
                          name='lastName'
                          placeholder={formatMessage(formMessages.lastName)}
                          required
                        />
                      </Flex>
                    </Col>
                    <Col>
                      <InputField
                        autoComplete='email'
                        borderColorVariant='lightGray'
                        colorVariant='secondaryLightGrey'
                        disabled={disabledPersonal}
                        maxLength={255}
                        name='email'
                        placeholder={formatMessage(formMessages.email)}
                        required
                        type='email'
                      />
                    </Col>
                    {(!disabledPersonal || phone) && (
                      <Col>
                        <S.PhoneInputWrapper>
                          <SelectField
                            aria-label={formatMessage(formMessages.phone)}
                            borderColorVariant='lightGray'
                            colorVariant='secondaryLightGrey'
                            isDisabled={disabledPersonal}
                            name='phonePrefix'
                            options={phonePrefixSelectOpts}
                            styles={{
                              placeholder: (baseStyles) => ({
                                ...baseStyles,
                                color: selectPlaceholderColor,
                              }),
                              control: (baseStyles) => ({
                                ...baseStyles,
                                minHeight: '39px',
                              }),
                            }}
                          />
                          <Flex flexDirection='column'>
                            <InputField
                              autoComplete='phone'
                              borderColorVariant='lightGray'
                              colorVariant='secondaryLightGrey'
                              disabled={disabledPersonal}
                              maxLength={80}
                              name='phone'
                              pattern='[0-9]+'
                              placeholder={formatMessage(
                                formMessages.telephoneNumber,
                              )}
                              type='text'
                            />
                            <Typography
                              color='secondaryGrey'
                              scale='p3'
                              textAlign='right'
                            >
                              {examplePhone}
                            </Typography>
                          </Flex>
                        </S.PhoneInputWrapper>
                      </Col>
                    )}
                    <Col>
                      <TextAreaField
                        borderColorVariant='lightGray'
                        colorVariant='secondaryLightGrey'
                        maxLength={SALESFORCE_TEXT_MAX_LENGTH}
                        name='comments'
                        placeholder={formatMessage(messages.wouldLikeTo)}
                        rows={5}
                      />
                    </Col>

                    <Col md={12}>
                      <Typography
                        color='secondaryGrey'
                        scale='p3'
                        textAlign='right'
                      >
                        {formatMessage(formMessages.mandatoryFields)}
                      </Typography>
                    </Col>
                    <Col>
                      <CheckboxField name='newsletterOptIn'>
                        <Box>
                          <Typography>{KeepInformed}</Typography>
                          <Typography mt={2}>{PersonalDataPolicy}</Typography>
                          <Typography mt={2}>{ConfirmLegal}</Typography>
                        </Box>
                      </CheckboxField>
                    </Col>
                    <Col>
                      <ReCaptcha
                        ref={reCaptchaRef}
                        languageCode={languageCode}
                        name='captcha'
                        onChange={setIsVerifyingRecaptcha}
                        validation={false}
                      />
                    </Col>
                    <Col>
                      {error && (
                        <Typography color='error' textAlign='center'>
                          {error}
                        </Typography>
                      )}
                    </Col>
                    <Col>
                      <Flex justifyContent='center'>
                        <LegacyButton
                          disabled={
                            processing || !captcha || isVerifyingRecaptcha
                          }
                          isProcessing={processing || isVerifyingRecaptcha}
                          label={formatMessage(commonMessages.submit)}
                          type='submit'
                        />
                      </Flex>
                    </Col>
                  </S.FormWrapper>
                </FormProvider>
              </Box>
            </Visibility>
          </Box>
        )}
      </Modal.Body>
    </Modal>
  );
};
